/**
 * Metro counties that publish an open (no-token) easement, right-of-way,
 * parcel or plat ArcGIS layer. Every endpoint, layer name, projection, record count and
 * sample row below was read live from the county's own service on 2026-10-02;
 * the same layers already feed the site's county easement lookups.
 */
import type { CountyRecordHub } from "./countyRecordHubs";

export interface OpenGisSeed {
  id: string;
  county: string;
  state: string;
  stateName: string;
  fips: string;
  recorderName: string;
  recorderSearch: string;
  gisName: string;
  gisViewer: string;
  serviceUrl: string;
  layerId: number;
  layerName: string;
  recordCount: number;
  projection: string;
  summary: string;
  reliabilityScore: number;
  /** Defaults to "Easement GIS"; parcel-only services say "Parcel GIS". */
  assetClass?: "Easement GIS" | "Parcel GIS";
  /** Browser link when the county disables the HTML services directory (JSON still answers). */
  browseUrl?: string;
  sampleRecord: Record<string, string>;
  geometryType?: string;
}

const VERIFIED = "2026-10-02";

const SEEDS: OpenGisSeed[] = [
  {
    id: "il-dupage",
    county: "DuPage County",
    state: "IL",
    stateName: "Illinois",
    fips: "17043",
    recorderName: "DuPage County Clerk Recordings",
    recorderSearch:
      "https://www.dupagecounty.gov/government/elected_officials/county_clerk/recordings/",
    gisName: "DuPage County GIS",
    gisViewer: "https://gis.dupageco.org/",
    serviceUrl: "https://gis.dupageco.org/arcgis/rest/services/OpenData/ROW/MapServer",
    layerId: 0,
    layerName: "ROW",
    recordCount: 5940,
    projection: "EPSG:3435 (IL State Plane East, feet)",
    summary:
      "County Clerk handles recordings. DuPage GIS publishes an open right-of-way polygon layer with recording document fields.",
    reliabilityScore: 90,
    sampleRecord: { OBJECTID: "17231", ROW_TYPE: "4" },
  },
  {
    id: "fl-sarasota",
    county: "Sarasota County",
    state: "FL",
    stateName: "Florida",
    fips: "12115",
    recorderName: "Sarasota Clerk Official Records",
    recorderSearch: "https://secure.sarasotaclerk.com/OfficialRecords.aspx",
    gisName: "Sarasota County GIS",
    gisViewer: "https://www.scgov.net/government/geographic-information-systems",
    serviceUrl: "https://ags3.scgov.net/server/rest/services/Hosted/Easement/FeatureServer",
    layerId: 0,
    layerName: "Easement",
    recordCount: 5384,
    projection: "EPSG:2882 (FL West, feet)",
    summary:
      "Clerk's official records search covers recorded easements. County GIS publishes an open easement polygon layer with instrument notes.",
    reliabilityScore: 88,
    sampleRecord: { objectid: "1", notes: "2000103273, SWFMD", easement_type: "3" },
  },
  {
    id: "mn-ramsey",
    county: "Ramsey County",
    state: "MN",
    stateName: "Minnesota",
    fips: "27123",
    recorderName: "Ramsey County Property Records",
    recorderSearch: "https://propertyrecords.co.ramsey.mn.us/Web/sasearch.aspx?section=",
    gisName: "Ramsey County Maps & Surveys",
    gisViewer: "https://www.ramseycounty.us/residents/property-home/maps-surveys",
    serviceUrl: "https://maps.co.ramsey.mn.us/arcgis/rest/services/OpenData/OpenData/FeatureServer",
    layerId: 15,
    layerName: "Easements",
    recordCount: 13449,
    projection: "EPSG:26915 (UTM 15N, meters)",
    summary:
      "County property records search covers abstract and Torrens documents. Open data includes an easement polygon layer with stated areas.",
    reliabilityScore: 88,
    sampleRecord: {
      OBJECTID: "8535382",
      Name: "Walkway",
      EasementSubtype: "Pedestrian",
      StatedArea: "1814 sq.ft",
    },
  },
  {
    id: "va-fairfax",
    county: "Fairfax County",
    state: "VA",
    stateName: "Virginia",
    fips: "51059",
    recorderName: "Fairfax Circuit Court Land Records (CPAN)",
    recorderSearch:
      "https://www.fairfaxcounty.gov/circuit/online-services/court-public-access-network",
    gisName: "Fairfax County GIS Open Data",
    gisViewer: "https://data-fairfaxcountygis.opendata.arcgis.com/",
    serviceUrl:
      "https://services1.arcgis.com/ioennV6PpG5Xodq0/ArcGIS/rest/services/OpenData_A2/FeatureServer",
    layerId: 4,
    layerName: "Stormwater Easements",
    recordCount: 50116,
    projection: "EPSG:2283 (VA North, feet)",
    summary:
      "Circuit Court land records are searchable through CPAN (subscription). Open data includes stormwater easements with deed book and page.",
    reliabilityScore: 86,
    sampleRecord: {
      OBJECTID: "1",
      ESMTNAME: "STORM DRAINAGE",
      DEEDBOOK: "08681",
      DEEDPAGE: "0759",
    },
  },
  {
    id: "fl-leon",
    county: "Leon County",
    state: "FL",
    stateName: "Florida",
    fips: "12073",
    recorderName: "Leon County Clerk Official Records",
    recorderSearch:
      "https://cvweb.leonclerk.com/public/clerk_services/official_records/official_records.asp",
    gisName: "Tallahassee-Leon County GIS",
    gisViewer: "https://www.tlcgis.org/",
    serviceUrl:
      "https://intervector.leoncountyfl.gov/intervector/rest/services/MapServices/TLC_OverlayEasement_Feature_WM_D/MapServer",
    layerId: 0,
    layerName: "Easements",
    recordCount: 18144,
    projection: "EPSG:3857 (Web Mercator)",
    summary:
      "Clerk's official records search covers recorded easements. TLCGIS publishes an easement layer tied to official record book and page.",
    reliabilityScore: 90,
    sampleRecord: {
      OBJECTID: "1",
      EASE_NUM: "14772",
      INST_TYPE: "OFFICIAL RECORD BOOK",
      BOOK: "4581",
      PAGE: "600",
    },
  },
  {
    id: "va-chesterfield",
    county: "Chesterfield County",
    state: "VA",
    stateName: "Virginia",
    fips: "51041",
    recorderName: "Chesterfield Circuit Court Land Records",
    recorderSearch: "https://www.ccclandrecords.org/",
    gisName: "Chesterfield County GIS",
    gisViewer: "https://www.chesterfield.gov/341/Geographic-Information-Services",
    serviceUrl:
      "https://services3.arcgis.com/TsynfzBSE6sXfoLq/ArcGIS/rest/services/Cadastral/FeatureServer",
    layerId: 13,
    layerName: "Ingress/Egress Easement",
    recordCount: 1883,
    projection: "EPSG:3857 (Web Mercator)",
    summary:
      "Circuit Court land records are searchable online. County GIS publishes ingress/egress easements with plat book fields.",
    reliabilityScore: 86,
    sampleRecord: { OBJECTID: "1", Name: "Ingress Egress", StatedArea: "0.147 ac" },
  },
  // Next 25 metro counties: open parcel, easement or plat layers, read live 2026-10-02.
  {
    id: "pa-allegheny",
    county: "Allegheny County",
    state: "PA",
    stateName: "Pennsylvania",
    fips: "42003",
    recorderName: "Allegheny Department of Real Estate",
    recorderSearch: "https://pa_allegheny.uslandrecords.com/palr/",
    gisName: "Allegheny County GIS",
    gisViewer: "https://www.alleghenycounty.us/Services/Information-Technology/GIS",
    serviceUrl:
      "https://gisdata.alleghenycounty.us/arcgis/rest/services/OPENDATA/Parcels/MapServer",
    layerId: 0,
    layerName: "Parcel",
    recordCount: 580039,
    projection: "EPSG:2272 (PA South, feet)",
    assetClass: "Parcel GIS",
    summary:
      "Official records search covers recorded deeds and easements. The county's open map service publishes 580,039 parcel polygons.",
    reliabilityScore: 84,
    sampleRecord: { PIN: "0102P00193000000", MAPBLOCKLOT: "102-P-193", CALCACREAGE: "0.07" },
  },
  {
    id: "fl-pinellas",
    county: "Pinellas County",
    state: "FL",
    stateName: "Florida",
    fips: "12103",
    recorderName: "Pinellas Clerk Official Records",
    recorderSearch: "https://officialrecords.mypinellasclerk.gov/",
    gisName: "Pinellas County GIS",
    gisViewer: "https://www.pinellas.gov/gis/",
    serviceUrl: "https://egis.pinellas.gov/gis/rest/services/PublicWebGIS/RightOfWay/MapServer",
    layerId: 3,
    layerName: "Utility Easement",
    recordCount: 3169,
    projection: "EPSG:2882 (FL West, feet)",
    assetClass: "Easement GIS",
    summary:
      "Official records search covers recorded deeds and easements. The county's open map service publishes 3,169 easement polygons.",
    reliabilityScore: 84,
    sampleRecord: { ROWID_: "OR 3416-750", ROWTYPE: "UTILITY EASEMENT" },
  },
  {
    id: "tx-denton",
    county: "Denton County",
    state: "TX",
    stateName: "Texas",
    fips: "48121",
    recorderName: "Denton County Public Search",
    recorderSearch: "https://denton.tx.publicsearch.us/",
    gisName: "Denton County GIS",
    gisViewer: "https://www.dentoncounty.gov/369/GIS",
    browseUrl: "https://gis.dentoncounty.gov/arcgis/rest/services/Parcels/MapServer?f=pjson",
    serviceUrl: "https://gis.dentoncounty.gov/arcgis/rest/services/Parcels/MapServer",
    layerId: 0,
    layerName: "Parcels",
    recordCount: 304723,
    projection: "EPSG:2276 (TX North Central, feet)",
    assetClass: "Parcel GIS",
    summary:
      "Official records search covers recorded deeds and easements. The county's open map service publishes 304,723 parcel polygons.",
    reliabilityScore: 84,
    sampleRecord: { PROP_TYPE: "R", LEGAL_DESC: "TIMBER MESA BLK C LOT 5", BLOCK: "C", LOT: "5" },
  },
  {
    id: "ga-dekalb",
    county: "DeKalb County",
    state: "GA",
    stateName: "Georgia",
    fips: "13089",
    recorderName: "GSCCCA Statewide Search",
    recorderSearch: "https://search.gsccca.org/",
    gisName: "DeKalb County GIS map service",
    gisViewer: "https://dcgis.dekalbcountyga.gov/hosted/rest/services/Parcels/MapServer",
    serviceUrl: "https://dcgis.dekalbcountyga.gov/hosted/rest/services/Parcels/MapServer",
    layerId: 0,
    layerName: "Tax Parcels",
    recordCount: 246205,
    projection: "EPSG:3857 (Web Mercator)",
    assetClass: "Parcel GIS",
    summary:
      "Official records search covers recorded deeds and easements. The county's open map service publishes 246,205 parcel polygons.",
    reliabilityScore: 84,
    sampleRecord: { STATEDAREA: "41799", CVTTXDSCRP: "UNINCORPORATED" },
  },
  {
    id: "ca-contra-costa",
    county: "Contra Costa County",
    state: "CA",
    stateName: "California",
    fips: "06013",
    recorderName: "Contra Costa Clerk-Recorder",
    recorderSearch: "https://crsecurepayment.com/RW/",
    gisName: "Contra Costa CCMAP service",
    gisViewer:
      "https://gis.cccounty.us/arcgis/rest/services/CCMAP/Assessment_Parcels_ArcPro/MapServer",
    serviceUrl:
      "https://gis.cccounty.us/arcgis/rest/services/CCMAP/Assessment_Parcels_ArcPro/MapServer",
    layerId: 0,
    layerName: "Assessment Parcels",
    recordCount: 387860,
    projection: "EPSG:3857 (Web Mercator)",
    assetClass: "Parcel GIS",
    summary:
      "Official records search covers recorded deeds and easements. The county's open map service publishes 387,860 parcel polygons.",
    reliabilityScore: 84,
    sampleRecord: { APN: "560270047", TRA: "08001" },
  },
  {
    id: "la-east-baton-rouge",
    county: "East Baton Rouge Parish",
    state: "LA",
    stateName: "Louisiana",
    fips: "22033",
    recorderName: "EBR Clerk of Court Records",
    recorderSearch: "https://clerkconnect.com/recordSearch/ebr",
    gisName: "EBRGIS maps",
    gisViewer: "https://maps.brla.gov/",
    serviceUrl: "https://maps.brla.gov/gis/rest/services/Cadastral/Lot_Lookup/MapServer",
    layerId: 0,
    layerName: "Lot Lookup",
    recordCount: 198352,
    projection: "EPSG:3452 (LA South, feet)",
    assetClass: "Parcel GIS",
    summary:
      "Official records search covers recorded deeds and easements. The county's open map service publishes 198,352 parcel polygons.",
    reliabilityScore: 84,
    sampleRecord: { ID: "1", LOT_ID_TEXT: "631040295" },
  },
  {
    id: "fl-lee",
    county: "Lee County",
    state: "FL",
    stateName: "Florida",
    fips: "12071",
    recorderName: "Lee Clerk Official Records",
    recorderSearch:
      "https://www.leeclerk.org/departments/official-records-services/search-official-records",
    gisName: "Lee County Property Appraiser",
    gisViewer: "https://www.leepa.org/",
    serviceUrl: "https://gissvr.leepa.org/gissvr/rest/services/ParcelsWFS/MapServer",
    layerId: 0,
    layerName: "FabricParcels",
    recordCount: 564734,
    projection: "EPSG:4326 (WGS 84)",
    assetClass: "Parcel GIS",
    summary:
      "Official records search covers recorded deeds and easements. The county's open map service publishes 564,734 parcel polygons.",
    reliabilityScore: 84,
    sampleRecord: { Name: "344725B40480C00CE", StatedArea: "0.12982093 ac" },
  },
  {
    id: "mi-kent",
    county: "Kent County",
    state: "MI",
    stateName: "Michigan",
    fips: "26081",
    recorderName: "Kent County Online Deeds Search",
    recorderSearch: "https://www.kentcountymi.gov/1892/Online-Deeds-Search",
    gisName: "Kent County GIS",
    gisViewer: "https://www.kentcountymi.gov/297/Geographic-Information-Systems",
    serviceUrl: "https://gis.kentcountymi.gov/agisprod/rest/services/ParcelsWithCondos/MapServer",
    layerId: 0,
    layerName: "Parcels With Condos",
    recordCount: 232075,
    projection: "Michigan State Plane (per service)",
    assetClass: "Parcel GIS",
    summary:
      "Official records search covers recorded deeds and easements. The county's open map service publishes 232,075 parcel polygons.",
    reliabilityScore: 84,
    sampleRecord: { PPN: "410106100019", TAXPARCEL_FLAG: "Yes" },
  },
  {
    id: "ny-erie",
    county: "Erie County",
    state: "NY",
    stateName: "New York",
    fips: "36029",
    recorderName: "Erie County Clerk Records",
    recorderSearch: "https://ecclerk.erie.gov/recordsng_web/search_help.html",
    gisName: "Erie County GIS map service",
    gisViewer: "https://gis.erie.gov/server/rest/services/OGIS/Parcels/MapServer",
    serviceUrl: "https://gis.erie.gov/server/rest/services/OGIS/Parcels/MapServer",
    layerId: 0,
    layerName: "Parcels",
    recordCount: 360170,
    projection: "EPSG:2262 (NY West, feet)",
    assetClass: "Parcel GIS",
    summary:
      "Official records search covers recorded deeds and easements. The county's open map service publishes 360,170 parcel polygons.",
    reliabilityScore: 84,
    sampleRecord: { SWIS: "142489", SBL: "201.00-3-22", ASSESSACRE: "10.1" },
  },
  {
    id: "pa-montgomery",
    county: "Montgomery County",
    state: "PA",
    stateName: "Pennsylvania",
    fips: "42091",
    recorderName: "Montgomery County Recorder of Deeds",
    recorderSearch: "https://rodviewer.montcopa.org/countyweb/login.do?countyname=Montgomery",
    gisName: "Montgomery County GIS map service",
    gisViewer:
      "https://gis.montcopa.org/arcgis/rest/services/Parcels/Montgomery_County_Parcels/MapServer",
    serviceUrl:
      "https://gis.montcopa.org/arcgis/rest/services/Parcels/Montgomery_County_Parcels/MapServer",
    layerId: 10,
    layerName: "Montgomery County Parcels",
    recordCount: 310134,
    projection: "EPSG:2272 (PA South, feet)",
    assetClass: "Parcel GIS",
    summary:
      "Official records search covers recorded deeds and easements. The county's open map service publishes 310,134 parcel polygons.",
    reliabilityScore: 84,
    sampleRecord: { TAXPIN: "300034228005", PARCELTYPE: "BASE PARCEL", CALCACREAGE: "0.186" },
  },
  {
    id: "hi-honolulu",
    county: "Honolulu County",
    state: "HI",
    stateName: "Hawaii",
    fips: "15003",
    recorderName: "Hawaii Bureau of Conveyances",
    recorderSearch: "https://dlnr.hawaii.gov/boc/online-services/",
    gisName: "Honolulu DPP easements service",
    gisViewer:
      "https://services.arcgis.com/tNJpAOha4mODLkXz/arcgis/rest/services/DPP_Easements/FeatureServer",
    serviceUrl:
      "https://services.arcgis.com/tNJpAOha4mODLkXz/arcgis/rest/services/DPP_Easements/FeatureServer",
    layerId: 0,
    layerName: "DPP Easements",
    recordCount: 9902,
    projection: "EPSG:3760 (HI Zone 3, feet)",
    assetClass: "Easement GIS",
    summary:
      "Official records search covers recorded deeds and easements. The county's open map service publishes 9,902 easement polygons.",
    reliabilityScore: 84,
    sampleRecord: { esmt_id: "990", dpp_approved_area_sqft: "475" },
  },
  {
    id: "tx-el-paso",
    county: "El Paso County",
    state: "TX",
    stateName: "Texas",
    fips: "48141",
    recorderName: "El Paso County Official Public Records",
    recorderSearch: "https://apps.epcountytx.gov/publicrecords/OfficialPublicRecords",
    gisName: "City of El Paso parcel service",
    gisViewer: "https://gis.elpasotexas.gov/arcgis/rest/services/EPParcels/MapServer",
    serviceUrl: "https://gis.elpasotexas.gov/arcgis/rest/services/EPParcels/MapServer",
    layerId: 0,
    layerName: "EPParcels",
    recordCount: 391659,
    projection: "EPSG:2277 (TX Central, feet)",
    assetClass: "Parcel GIS",
    summary:
      "Official records search covers recorded deeds and easements. The county's open map service publishes 391,659 parcel polygons.",
    reliabilityScore: 84,
    sampleRecord: { PIDN: "G68699902003100", LOCADDRESS: "3207 MOREHEAD AVE" },
  },
  {
    id: "wi-milwaukee",
    county: "Milwaukee County",
    state: "WI",
    stateName: "Wisconsin",
    fips: "55079",
    recorderName: "Milwaukee Register of Deeds",
    recorderSearch: "https://county.milwaukee.gov/EN/Register-of-Deeds/Real-Estate-Records",
    gisName: "Milwaukee County Land Information Office",
    gisViewer: "https://county.milwaukee.gov/EN/Land-Information-Office",
    serviceUrl:
      "https://services2.arcgis.com/s1wgJQKbKJihhhaT/arcgis/rest/services/Milwaukee_County_Parcels_Property_Information_view/FeatureServer",
    layerId: 58,
    layerName: "Milwaukee County Property Information",
    recordCount: 280670,
    projection: "EPSG:6609 (WI South, feet)",
    assetClass: "Parcel GIS",
    summary:
      "Official records search covers recorded deeds and easements. The county's open map service publishes 280,670 parcel polygons.",
    reliabilityScore: 84,
    sampleRecord: { TAXKEY: "0090009000", PARCEL_KEY: "009-0009", MUNINAME: "Brown Deer" },
  },
  {
    id: "co-denver",
    county: "Denver County",
    state: "CO",
    stateName: "Colorado",
    fips: "08031",
    recorderName: "Denver Clerk and Recorder",
    recorderSearch:
      "https://www.denvergov.org/Government/Agencies-Departments-Offices/Agencies-Departments-Offices-Directory/Clerk-and-Recorder",
    gisName: "Denver Open Data",
    gisViewer: "https://www.denvergov.org/opendata",
    serviceUrl:
      "https://services1.arcgis.com/zdB7qR0BtYrg0Xpl/arcgis/rest/services/ODC_PROP_PARCELS_A/FeatureServer",
    layerId: 245,
    layerName: "PROP_PARCELS_A",
    recordCount: 240437,
    projection: "EPSG:2877 (CO Central, feet)",
    assetClass: "Parcel GIS",
    summary:
      "Official records search covers recorded deeds and easements. The county's open map service publishes 240,437 parcel polygons.",
    reliabilityScore: 84,
    sampleRecord: { SCHEDNUM: "0003100118000", MAPNUM: "00031", PARCELNUM: "118" },
  },
  {
    id: "wa-pierce",
    county: "Pierce County",
    state: "WA",
    stateName: "Washington",
    fips: "53053",
    recorderName: "Pierce County Auditor Recording",
    recorderSearch: "https://www.piercecountywa.gov/1006/Recording",
    gisName: "Pierce County tax parcel service",
    gisViewer:
      "https://services2.arcgis.com/1UvBaQ5y1ubjUPmd/arcgis/rest/services/Tax_Parcels/FeatureServer",
    serviceUrl:
      "https://services2.arcgis.com/1UvBaQ5y1ubjUPmd/arcgis/rest/services/Tax_Parcels/FeatureServer",
    layerId: 0,
    layerName: "Tax Parcels",
    recordCount: 339999,
    projection: "EPSG:2927 (WA South, feet)",
    assetClass: "Parcel GIS",
    summary:
      "Official records search covers recorded deeds and easements. The county's open map service publishes 339,999 parcel polygons.",
    reliabilityScore: 84,
    sampleRecord: { TaxParcelNumber: "0019012000", TaxParcelType: "Base Parcel" },
  },
  {
    id: "wa-snohomish",
    county: "Snohomish County",
    state: "WA",
    stateName: "Washington",
    fips: "53061",
    recorderName: "Snohomish County Auditor Recording",
    recorderSearch: "https://www.snohomishcountywa.gov/5492/Recording",
    gisName: "Snohomish County parcel service",
    gisViewer:
      "https://services6.arcgis.com/z6WYi9VRHfgwgtyW/arcgis/rest/services/All_Parcels_(Past_and_Present)/FeatureServer",
    serviceUrl:
      "https://services6.arcgis.com/z6WYi9VRHfgwgtyW/arcgis/rest/services/All_Parcels_(Past_and_Present)/FeatureServer",
    layerId: 0,
    layerName: "All_Parcels (past and present)",
    recordCount: 370392,
    projection: "EPSG:3857 (Web Mercator)",
    assetClass: "Parcel GIS",
    summary:
      "Official records search covers recorded deeds and easements. The county's open map service publishes 370,392 parcel polygons.",
    reliabilityScore: 84,
    sampleRecord: { PAR_OID: "112755", CREATEDATE: "2010-01-07", RETIREDATE: "2015-07-15" },
  },
  {
    id: "ga-gwinnett",
    county: "Gwinnett County",
    state: "GA",
    stateName: "Georgia",
    fips: "13135",
    recorderName: "GSCCCA Statewide Search",
    recorderSearch: "https://search.gsccca.org/realestate/propertysearch.asp",
    gisName: "Gwinnett County GIS",
    gisViewer: "https://www.gwinnettcounty.com/departments/informationtechnologyservices/gis",
    serviceUrl:
      "https://services3.arcgis.com/RfpmnkSAQleRbndX/arcgis/rest/services/Property_and_Tax/FeatureServer",
    layerId: 0,
    layerName: "Parcels",
    recordCount: 309818,
    projection: "EPSG:2240 (GA West, feet)",
    assetClass: "Parcel GIS",
    summary:
      "Official records search covers recorded deeds and easements. The county's open map service publishes 309,818 parcel polygons.",
    reliabilityScore: 84,
    sampleRecord: { PIN: "2004B001", LOT: "51", PARCELTYPE: "P" },
  },
  {
    id: "ca-alameda",
    county: "Alameda County",
    state: "CA",
    stateName: "California",
    fips: "06001",
    recorderName: "Alameda County Clerk-Recorder",
    recorderSearch: "https://www.acgov.org/auditor/clerk/opr/",
    gisName: "Alameda County maps",
    gisViewer: "https://www.acgov.org/maps/",
    serviceUrl:
      "https://services5.arcgis.com/ROBnTHSNjoZ2Wm1P/arcgis/rest/services/Parcels/FeatureServer",
    layerId: 0,
    layerName: "Parcel Boundaries",
    recordCount: 489636,
    projection: "EPSG:3857 (Web Mercator)",
    assetClass: "Parcel GIS",
    summary:
      "Official records search covers recorded deeds and easements. The county's open map service publishes 489,636 parcel polygons.",
    reliabilityScore: 84,
    sampleRecord: { APN: "15-1338-24", BOOK: "15", PAGE: "1338" },
  },
  {
    id: "nv-washoe",
    county: "Washoe County",
    state: "NV",
    stateName: "Nevada",
    fips: "32031",
    recorderName: "Washoe County Recorder",
    recorderSearch: "https://icris.washoecounty.us/ssrecorder/",
    gisName: "Washoe County open data service",
    gisViewer:
      "https://services.arcgis.com/iCGWaR7ZHc5saRIl/arcgis/rest/services/Nightly_OpenData_Update/FeatureServer",
    serviceUrl:
      "https://services.arcgis.com/iCGWaR7ZHc5saRIl/arcgis/rest/services/Nightly_OpenData_Update/FeatureServer",
    layerId: 1,
    layerName: "Parcels",
    recordCount: 194122,
    projection: "EPSG:3857 (Web Mercator)",
    assetClass: "Parcel GIS",
    summary:
      "Official records search covers recorded deeds and easements. The county's open map service publishes 194,122 parcel polygons.",
    reliabilityScore: 84,
    sampleRecord: { APN: "102000", PIN: "001-020-00", SUBNAME: "WINDERMERE 1" },
  },
  {
    id: "id-ada",
    county: "Ada County",
    state: "ID",
    stateName: "Idaho",
    fips: "16001",
    recorderName: "Ada County Clerk Property Records",
    recorderSearch: "https://adacounty.id.gov/clerk/property-records/",
    gisName: "Ada County Assessor maps",
    gisViewer: "https://adacounty.id.gov/assessor/maps/",
    serviceUrl:
      "https://services2.arcgis.com/dgGjZc6xAH5m5JyP/arcgis/rest/services/Parcels/FeatureServer",
    layerId: 5,
    layerName: "Parcel",
    recordCount: 240373,
    projection: "Idaho State Plane (per service)",
    assetClass: "Parcel GIS",
    summary:
      "Official records search covers recorded deeds and easements. The county's open map service publishes 240,373 parcel polygons.",
    reliabilityScore: 84,
    sampleRecord: { PARCEL: "O1425830402", PROPYEAR: "2026", PROPCODE: "C" },
  },
  {
    id: "oh-hamilton",
    county: "Hamilton County",
    state: "OH",
    stateName: "Ohio",
    fips: "39061",
    recorderName: "Hamilton County Recorder",
    recorderSearch: "https://acclaim-web.hamiltoncountyohio.gov/AcclaimWebLive",
    gisName: "Hamilton County Auditor",
    gisViewer: "https://www.hamiltoncountyauditor.org/",
    serviceUrl:
      "https://services8.arcgis.com/YU1yCuZZBuqsMM2h/arcgis/rest/services/Parcel_Polygons_20241008_view/FeatureServer",
    layerId: 0,
    layerName: "Parcel polygons",
    recordCount: 418487,
    projection: "EPSG:3735 (OH South, feet)",
    assetClass: "Parcel GIS",
    summary:
      "Official records search covers recorded deeds and easements. The county's open map service publishes 418,487 parcel polygons.",
    reliabilityScore: 84,
    sampleRecord: { NAME: "057300070120", BOOK: "573", PAGE: "0007" },
  },
  {
    id: "ks-sedgwick",
    county: "Sedgwick County",
    state: "KS",
    stateName: "Kansas",
    fips: "20173",
    recorderName: "Sedgwick County Register of Deeds",
    recorderSearch: "https://roddocs.sedgwickcounty.org/RODSearch/Login.aspx",
    gisName: "Sedgwick County land records service",
    gisViewer:
      "https://services7.arcgis.com/McLat6HlPl45bNBv/arcgis/rest/services/Sedgwick_County_Land_Records/FeatureServer",
    serviceUrl:
      "https://services7.arcgis.com/McLat6HlPl45bNBv/arcgis/rest/services/Sedgwick_County_Land_Records/FeatureServer",
    layerId: 1,
    layerName: "Subdivisions",
    recordCount: 8340,
    projection: "EPSG:3420 (KS South, feet)",
    assetClass: "Parcel GIS",
    summary:
      "Official records search covers recorded deeds and easements. The county's open map service publishes 8,340 recorded plat polygons.",
    reliabilityScore: 84,
    sampleRecord: { PlatSubID: "T2192", RDBKPGNO: "PC 222-10", SubdivNM: "Schnitzler Farms Add" },
  },
  {
    id: "mi-oakland",
    county: "Oakland County",
    state: "MI",
    stateName: "Michigan",
    fips: "26125",
    recorderName: "Oakland County Register of Deeds",
    recorderSearch: "https://www.ocmideeds.com/",
    gisName: "Oakland County open parcel service",
    gisViewer:
      "https://gisservices.oakgov.com/arcgis/rest/services/Enterprise/EnterpriseOpenParcelDataMapService/MapServer",
    serviceUrl:
      "https://gisservices.oakgov.com/arcgis/rest/services/Enterprise/EnterpriseOpenParcelDataMapService/MapServer",
    layerId: 1,
    layerName: "Tax Parcel Plus",
    recordCount: 490640,
    projection: "EPSG:3857 (Web Mercator)",
    assetClass: "Parcel GIS",
    summary:
      "Official records search covers recorded deeds and easements. The county's open map service publishes 490,640 parcel polygons.",
    reliabilityScore: 84,
    sampleRecord: { KEYPIN: "2536483012", CVTTAXDESCRIPTION: "CITY OF HAZEL PARK" },
  },
  {
    id: "va-loudoun",
    county: "Loudoun County",
    state: "VA",
    stateName: "Virginia",
    fips: "51107",
    recorderName: "Loudoun Circuit Court Land Records",
    recorderSearch: "https://www.loudoun.gov/1360/Land-Records",
    gisName: "Loudoun County GIS",
    gisViewer: "https://www.loudoun.gov/gis",
    serviceUrl: "https://logis.loudoun.gov/gis/rest/services/COL_WebMercator/Parcels4DIT/MapServer",
    layerId: 0,
    layerName: "Parcels",
    recordCount: 132898,
    projection: "EPSG:3857 (Web Mercator)",
    assetClass: "Parcel GIS",
    summary:
      "Official records search covers recorded deeds and easements. The county's open map service publishes 132,898 parcel polygons.",
    reliabilityScore: 84,
    sampleRecord: { PA_PLAT_NUM: "2017-0404", PA_PLAT_LOT: "A2B", PA_LEGAL_ACRE: "1.56" },
  },
  {
    id: "fl-polk",
    county: "Polk County",
    state: "FL",
    stateName: "Florida",
    fips: "12105",
    recorderName: "Polk Clerk Official Records",
    recorderSearch: "https://apps.polkcountyclerk.net/browserviewor/",
    gisName: "Polk Property Appraiser map service",
    gisViewer:
      "https://gis.polk-county.net/server/rest/services/Map_Property_Appraiser/FeatureServer",
    serviceUrl:
      "https://gis.polk-county.net/server/rest/services/Map_Property_Appraiser/FeatureServer",
    layerId: 1,
    layerName: "Parcels",
    recordCount: 438616,
    projection: "EPSG:3857 (Web Mercator)",
    assetClass: "Parcel GIS",
    summary:
      "Official records search covers recorded deeds and easements. The county's open map service publishes 438,616 parcel polygons.",
    reliabilityScore: 84,
    sampleRecord: { PARCELID: "222601000000014010", SECTION: "01", TOWNSHIP: "26", RANGE: "22" },
  },
];

export function hubFromOpenGisSeed(s: OpenGisSeed, verified = VERIFIED): Omit<CountyRecordHub, "title"> {
  return {
  id: s.id,
  county: s.county,
  state: s.state,
  stateName: s.stateName,
  fips: s.fips,
  availability: ["arcgis", "portal"],
  assetClasses: [s.assetClass ?? "Easement GIS", "Recorder/Deeds"],
  links: { recorderSearch: s.recorderSearch, gisViewer: s.gisViewer, restEndpoint: s.serviceUrl, ...(s.browseUrl ? { restBrowse: s.browseUrl } : {}) },
  summary: s.summary,
  lastVerified: verified,
  reliabilityScore: s.reliabilityScore,
  deedIndexUrl: s.recorderSearch,
  portals: [
    { name: s.recorderName, access: "free", feasibility: "medium", url: s.recorderSearch },
    { name: s.gisName, access: "free", feasibility: "medium", url: s.gisViewer },
    {
      name: `${s.county} ArcGIS REST service`,
      access: "free",
      feasibility: "high",
      url: s.browseUrl ?? s.serviceUrl,
    },
  ],
  restLayers: [
    {
      id: s.layerId,
      name: s.layerName,
      geometryType: s.geometryType ?? "esriGeometryPolygon",
      recordCount: s.recordCount,
    },
  ],
  scan: {
    durationMs: 0,
    layersDiscovered: 1,
    recordsSampled: 1,
    lastPublished: verified,
    format: "Esri JSON / GeoJSON",
    fields: [
      {
        label: "Service type",
        value: `${s.serviceUrl.includes("FeatureServer") ? "FeatureServer" : "MapServer"} (public, no token)`,
      },
      {
        label: s.assetClass === "Parcel GIS" ? "Map layer" : "Easement layer",
        value: `Layer ${s.layerId}: ${s.layerName}`,
      },
      { label: "Projection", value: s.projection },
    ],
    sampleRecord: s.sampleRecord,
  },
};
}

export const OPEN_GIS_COUNTY_HUBS: Omit<CountyRecordHub, "title">[] = SEEDS.map((s) => hubFromOpenGisSeed(s));

